/**
 * UFT Page Scroll Lock: freezes page scrolling while a drawer or the age gate is open without the page shifting.
 * Hiding the body's overflow removes a classic scrollbar, so the lock pads the body by the scrollbar's width to keep
 * the layout where it was. Each modal holds the lock under its own name, and the page scrolls again only when the last
 * holder lets go.
 *
 * Usage:
 *   window.UFT.PageScrollLock.lock('drawer-cart');
 *   window.UFT.PageScrollLock.unlock('drawer-cart');
 */

window.UFT = window.UFT || {};
window.UFT.PageScrollLock =
	window.UFT.PageScrollLock ||
	(function () {
		class PageScrollLock {
			// A class, not an inline overflow style: third-party embeds reset `body.style.overflow`
			// (Videowise clears it on happyhippo.com's home page about 100 ms after the age gate
			// locks), which wiped an inline lock and let the page scroll behind the gate. Not the
			// generic .overflow--hidden either: the PDP media modal removes that one on close.
			static #LOCKED_CLASS = 'page-scroll-locked';

			// Named, not counted: a repeated lock or a stray unlock from one holder (a double
			// close, a disconnect after a close) must never release another holder's lock.
			#holders = new Set();
			// Locks outside this helper (the PDP media modal) set the same body padding, and a delayed
			// age gate can open over one. Restoring what was there, not clearing it, leaves that lock's
			// padding in place when the gate closes first.
			#paddingBeforeLock = null;

			/**
			 * @param {string} name - Identifies the holder. Locking again under a held name does nothing.
			 */
			lock(name) {
				if (!this._isValidName(name) || this.#holders.has(name)) {
					return;
				}

				this.#holders.add(name);
				if (this.#holders.size === 1) {
					this._applyLock();
				}
			}

			/**
			 * @param {string} name - The holder to drop. Unlocking an unheld name does nothing.
			 */
			unlock(name) {
				if (!this.#holders.delete(name)) {
					return;
				}

				if (this.#holders.size === 0) {
					this._removeLock();
				}
			}

			// Measured only on the first hold: re-measuring under the lock reads an already
			// overflow-hidden body, reports zero, and jumps the page by the scrollbar's width.
			// Padding rather than `scrollbar-gutter: stable`: the gutter leaves fixed and top-layer
			// boxes (a drawer overlay, a modal's backdrop) 15px short of the viewport edge, and it
			// reserves space on pages with no scrollbar too.
			_applyLock() {
				this.#paddingBeforeLock = document.body.style.paddingRight;
				const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
				if (scrollbarWidth > 0) {
					document.body.style.paddingRight = `${scrollbarWidth}px`;
				}
				document.body.classList.add(PageScrollLock.#LOCKED_CLASS);
			}

			_removeLock() {
				document.body.classList.remove(PageScrollLock.#LOCKED_CLASS);
				document.body.style.paddingRight = this.#paddingBeforeLock;
				this.#paddingBeforeLock = null;
			}

			// Reported, not swallowed: a nameless lock is a caller bug, and it could never be unlocked.
			_isValidName(name) {
				if (typeof name === 'string' && name.length > 0) {
					return true;
				}

				console.error('[PageScrollLock] A lock needs a non-empty name.', name);
				return false;
			}
		}

		return new PageScrollLock();
	})();
